import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { MessageCircle, ArrowRight, RotateCcw, CheckCircle2 } from "lucide-react";
import PkiVoiceRecorder from "./PkiVoiceRecorder";
import PkiNbaSummary from "./PkiNbaSummary";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * KI-Interview-Modus · geführter Dialog mit 12 Kernfragen.
 * Nach Abschluss: SIS + Maßnahmenplan + NBA + Pflegegrad-Tendenz + Zusammenfassung.
 *
 * Schritte:
 *   1) Optional Kunde-Label eingeben → "Interview starten"
 *   2) Frage stellen → Antwort eingeben → "Weiter"
 *   3) Nach Frage 12 → "Auswertung erzeugen" → finalize Endpoint
 */
export default function PkiInterviewModus({ onUsageChange }) {
  const [phase, setPhase] = useState("intro"); // intro · chat · finalizing · done
  const [kundeLabel, setKundeLabel] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);  // {role, text, kategorie?, step?}
  const [currentQ, setCurrentQ] = useState(null);  // {step, total, frage, kategorie, hilfe_text, id}
  const [currentAnswer, setCurrentAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, currentQ, result]);

  const start = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/pflege-ki/interview/start`,
        { kunde_label: kundeLabel.trim() || null },
        { withCredentials: true });
      setSessionId(r.data.session_id);
      setCurrentQ(r.data.next);
      setMessages([
        { role: "bot", text: `Wir gehen jetzt ${r.data.next.total} Kernfragen durch. Du kannst Antworten kurz und stichpunktartig formulieren – die KI baut daraus später SIS und Maßnahmenplan.` },
        { role: "bot", step: r.data.next.step, kategorie: r.data.next.kategorie, text: r.data.next.frage, hilfe: r.data.next.hilfe_text },
      ]);
      setPhase("chat");
      setCurrentAnswer("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Interview konnte nicht gestartet werden.");
    } finally {
      setBusy(false);
    }
  };

  const submitAnswer = async () => {
    if (!currentAnswer.trim() || !sessionId || !currentQ) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/pflege-ki/interview/answer`, {
        session_id: sessionId,
        step: currentQ.step,
        antwort: currentAnswer.trim(),
      }, { withCredentials: true });
      const userMsg = { role: "user", text: currentAnswer.trim() };
      setMessages((prev) => [...prev, userMsg]);
      setCurrentAnswer("");

      if (r.data.next?.done) {
        setCurrentQ(null);
        setMessages((prev) => [...prev, {
          role: "bot",
          text: `Alle ${r.data.progress.total} Fragen beantwortet. Klick unten auf „Auswertung erzeugen“ – die KI baut jetzt SIS, Maßnahmenplan und NBA-Tendenz.`,
        }]);
      } else {
        const next = r.data.next;
        setCurrentQ(next);
        setMessages((prev) => [...prev, {
          role: "bot", step: next.step, kategorie: next.kategorie, text: next.frage, hilfe: next.hilfe_text,
        }]);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Antwort konnte nicht gespeichert werden.");
    } finally {
      setBusy(false);
    }
  };

  const finalize = async () => {
    if (!sessionId) return;
    setBusy(true);
    setPhase("finalizing");
    try {
      const r = await axios.post(`${API}/pflege-ki/interview/finalize`,
        { session_id: sessionId }, { withCredentials: true });
      setResult(r.data);
      setPhase("done");
      onUsageChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auswertung fehlgeschlagen.");
      setPhase("chat");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setPhase("intro");
    setSessionId(null);
    setMessages([]);
    setCurrentQ(null);
    setCurrentAnswer("");
    setResult(null);
    setKundeLabel("");
  };

  // ---- Render ----
  if (phase === "intro") {
    return (
      <div data-testid="pki-interview-intro" className="rounded-3xl border border-terra/30 bg-gradient-to-br from-peach-50 via-cream-50 to-white p-7 lg:p-9">
        <div className="inline-flex items-center gap-2 rounded-full bg-terra/15 text-terra px-3 h-7 text-[10.5px] uppercase tracking-[0.22em] font-bold mb-4">
          <MessageCircle className="h-3.5 w-3.5" strokeWidth={2} /> KI-Interview-Modus
        </div>
        <h2 className="font-display text-3xl text-sand-800 leading-tight">
          Neue SIS geführt starten
        </h2>
        <p className="mt-3 text-sand-700 text-[14.5px] leading-relaxed max-w-2xl">
          Die KI führt dich Schritt für Schritt durch 12 Kernfragen zur Pflegesituation.
          Nach der letzten Antwort erstellt sie automatisch SIS (A1–A6), Maßnahmenplan und
          eine NBA-Auswertung mit Pflegegrad-Tendenz.
        </p>
        <ul className="mt-4 space-y-1.5 text-[13.5px] text-sand-700">
          <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-sage-dark" strokeWidth={2} /> Stichpunkte reichen – KI strukturiert</li>
          <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-sage-dark" strokeWidth={2} /> 5–10 Minuten statt 30 Minuten Tippen</li>
          <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-sage-dark" strokeWidth={2} /> Mikrofon-Diktat verfügbar – Hände frei am Patientenbett</li>
          <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-sage-dark" strokeWidth={2} /> Alle Entwürfe brauchen fachliche Freigabe</li>
        </ul>
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3 max-w-lg">
          <input
            value={kundeLabel}
            onChange={(e) => setKundeLabel(e.target.value)}
            placeholder='Bezug (optional, z. B. "Frau M. · TK-007")'
            data-testid="pki-interview-kunde-label"
            className="flex-1 h-11 rounded-full border border-cream-300 bg-white px-4 text-[13.5px] text-sand-800 focus:outline-none focus:border-terra" />
          <Button onClick={start} disabled={busy}
            data-testid="pki-interview-start"
            className="rounded-full h-11 px-6 bg-terra hover:bg-terra-dark text-white font-semibold">
            {busy ? "Starte …" : "Interview starten"}
            <ArrowRight className="h-4 w-4 ml-2" strokeWidth={2} />
          </Button>
        </div>
        <p className="mt-5 text-[11.5px] text-sand-500 max-w-2xl leading-snug">
          Hinweis: <strong>Keine offizielle Pflegegrad-Einstufung.</strong> Die KI zeigt nur eine
          Tendenz auf Basis deiner Eingaben. Der MD entscheidet abschließend.
        </p>
      </div>
    );
  }

  if (phase === "done" && result) {
    return <InterviewResult result={result} onReset={reset} />;
  }

  // Chat phase (and finalizing overlay)
  const totalQ = currentQ?.total || 12;
  const stepDone = currentQ ? (currentQ.step - 1) : totalQ;
  const progress = Math.round((stepDone / totalQ) * 100);

  return (
    <div data-testid="pki-interview-chat" className="space-y-4">
      <div className="rounded-3xl border border-cream-300 bg-white p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark">
              KI-Interview · {kundeLabel || "ohne Bezug"}
            </div>
            <div className="text-[12px] text-sand-500 mt-0.5">
              Schritt {Math.min(stepDone + 1, totalQ)} von {totalQ}
            </div>
          </div>
          <Button onClick={reset} variant="outline" size="sm"
            data-testid="pki-interview-reset"
            className="h-9 rounded-full border-clay">
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" strokeWidth={2} />
            Neu starten
          </Button>
        </div>
        <div className="h-1.5 rounded-full bg-cream-200 overflow-hidden">
          <div className="h-full bg-terra transition-all" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Chat history */}
      <div className="rounded-3xl border border-cream-300 bg-cream-50 p-4 sm:p-5 max-h-[55vh] overflow-y-auto"
        data-testid="pki-interview-history">
        <div className="space-y-3">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[88%] rounded-2xl px-4 py-3 text-[13.5px] leading-relaxed ${
                m.role === "user"
                  ? "bg-sand-800 text-cream-50"
                  : "bg-white border border-cream-300 text-sand-800"
              }`}>
                {m.role === "bot" && m.step && (
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-terra mb-1">
                    Frage {m.step} · {m.kategorie}
                  </div>
                )}
                <div>{m.text}</div>
                {m.hilfe && (
                  <div className="mt-1.5 text-[11.5px] text-sand-500 italic">{m.hilfe}</div>
                )}
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      </div>

      {/* Input area */}
      {currentQ ? (
        <div className="rounded-3xl border border-cream-300 bg-white p-4 sm:p-5"
          data-testid="pki-interview-input-area">
          <label className="block text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-1.5">
            Deine Antwort (kurz)
          </label>
          <textarea
            rows={3}
            value={currentAnswer}
            onChange={(e) => setCurrentAnswer(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submitAnswer(); }
            }}
            placeholder="z. B. „Geht mit Rollator, letzter Sturz vor 4 Wochen, abends sehr unsicher."
            data-testid="pki-interview-answer"
            className="w-full rounded-xl border border-cream-300 bg-cream-50 px-3 py-2 text-[14px] text-sand-800 focus:outline-none focus:border-terra" />
          <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <PkiVoiceRecorder
                disabled={busy}
                onTranscript={(text) => {
                  setCurrentAnswer((prev) => (prev ? prev.trim() + " " : "") + text);
                }}
              />
              <span className="text-[11.5px] text-sand-500">oder ⌘/Strg + Enter zum Senden</span>
            </div>
            <Button onClick={submitAnswer} disabled={!currentAnswer.trim() || busy}
              data-testid="pki-interview-next"
              className="h-11 rounded-full bg-terra hover:bg-terra-dark text-white font-semibold px-6">
              {busy ? "Speichere …" : (currentQ.step >= totalQ ? "Letzte Antwort senden" : "Weiter")}
              <ArrowRight className="h-4 w-4 ml-2" strokeWidth={2} />
            </Button>
          </div>
        </div>
      ) : (
        <div className="rounded-3xl border border-sage-dark/40 bg-sage-dark/5 p-5 text-center"
          data-testid="pki-interview-finalize-area">
          <div className="font-display text-xl text-sand-800 mb-2">Alle Fragen beantwortet.</div>
          <p className="text-[13.5px] text-sand-600 mb-4">
            Klick unten – die KI erstellt jetzt SIS, Maßnahmenplan, NBA-Auswertung und
            zeigt eine Pflegegrad-Tendenz.
          </p>
          <Button onClick={finalize} disabled={busy}
            data-testid="pki-interview-finalize"
            className="h-12 rounded-full bg-sage-dark hover:bg-sage-dark/90 text-white font-semibold px-7">
            {busy ? "KI arbeitet … (~15 Sek.)" : "Auswertung erzeugen"}
            <ArrowRight className="h-4 w-4 ml-2" strokeWidth={2} />
          </Button>
        </div>
      )}

      {phase === "finalizing" && (
        <div className="rounded-2xl border border-cream-300 bg-cream-50 p-4 text-[13px] text-sand-700 text-center">
          KI erstellt SIS + Maßnahmenplan + NBA-Auswertung – das dauert etwa 10–20 Sekunden.
        </div>
      )}
    </div>
  );
}

function InterviewResult({ result, onReset }) {
  return (
    <div className="space-y-5" data-testid="pki-interview-result">
      <div className="rounded-3xl border border-sage-dark/40 bg-sage-dark/5 p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-sage-dark">
              Interview abgeschlossen
            </div>
            <h2 className="font-display text-2xl text-sand-800 mt-1">
              Auswertung · {result.kunde_label || "ohne Bezug"}
            </h2>
          </div>
          <Button onClick={onReset} variant="outline" size="sm"
            data-testid="pki-interview-reset-done"
            className="h-9 rounded-full border-clay">
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" strokeWidth={2} />
            Neues Interview
          </Button>
        </div>
      </div>

      {/* Pflegegrad-Tendenz + alle 6 NBA-Module + fehlende Folgefragen
          (gleiche Komponente wie im Freitext-Analyse-Tab) */}
      <PkiNbaSummary
        tendenz={result.pflegegrad_tendenz}
        modulesAll={result.modules_alle}
        missingFragen={result.missing_topics_fragen}
        testIdPrefix="pki-interview-nba"
      />

      <div className="rounded-2xl border border-cream-300 bg-white p-5"
        data-testid="pki-interview-next-steps">
        <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark mb-2">
          Empfohlene nächste Schritte
        </div>
        <ul className="space-y-2">
          {(result.naechste_schritte || []).map((s, i) => (
            <li key={i} className="flex items-start gap-2 text-[13px] text-sand-700">
              <ArrowRight className="h-4 w-4 mt-0.5 text-terra shrink-0" strokeWidth={2} />
              <span>{s}</span>
            </li>
          ))}
        </ul>
      </div>

      <DraftBlock label="SIS-Entwurf · A1–A6" testId="pki-interview-sis"
        draftId={result.sis?.draft_id} text={result.sis?.text} />
      <DraftBlock label="Maßnahmenplan" testId="pki-interview-massnahmen"
        draftId={result.massnahmenplan?.draft_id} text={result.massnahmenplan?.text} />

      <div className="rounded-2xl border border-brand-darkred/40 bg-peach-50 p-4 text-[12.5px] text-sand-800">
        <strong className="text-brand-darkred">Hinweis:</strong> {result.disclaimer}
        Beide Entwürfe sind in „Entwürfe" gespeichert und brauchen eine fachliche Freigabe via Haftungsmodal.
      </div>
    </div>
  );
}

function DraftBlock({ label, testId, draftId, text }) {
  if (!text) return null;
  return (
    <div className="rounded-2xl border border-cream-300 bg-white p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark">
          {label}
        </div>
        <span className="text-[11px] text-sand-500 font-mono">
          Draft-ID: {draftId?.slice(0, 8) || "—"}
        </span>
      </div>
      <pre className="whitespace-pre-wrap break-words text-[13px] leading-relaxed text-sand-800 font-mono bg-cream-50 rounded-xl p-4 border border-cream-200 max-h-[420px] overflow-y-auto">
        {text}
      </pre>
    </div>
  );
}
